'use client';

import type { ComponentType, PropsWithChildren, ReactNode } from 'react';
import { createElement } from 'react';

type ProviderComponent<P> = ComponentType<P>;

type ProviderSpecification<P> =
  | ProviderComponent<PropsWithChildren>
  | {
      provider: ProviderComponent<P>;
      properties: P;
    };

interface CompositeProviderProps<P extends Array<ProviderSpecification<any>>> {
  providers?: P;
}

const instantiateProvider = <P extends Record<string, unknown>>(
  specification: ProviderSpecification<P>,
  children: ReactNode,
) => {
  const childrenArray = Array.isArray(children) ? children : [children];

  if (typeof specification === 'function') {
    return createElement(specification, {}, ...childrenArray);
  } else {
    return createElement(specification.provider, specification.properties, ...childrenArray);
  }
};

const CompositeProvider = <P extends Array<ProviderSpecification<any>>>({
  providers,
  children,
}: PropsWithChildren<CompositeProviderProps<P>>) => {
  return providers?.reduceRight(
    (previous, current) => instantiateProvider(current, previous),
    children,
  );
};

export { CompositeProvider };
